import { useCallback, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  Tab,
  TabList,
  makeStyles,
} from '@fluentui/react-components';
import {
  ArrowLeft,
  Download,
  FolderTree,
  MoreHorizontal,
  RefreshCw,
  ScrollText,
  Upload,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import RouteFlight from '../components/RouteFlight';
import MigrationGroupFacet from '../components/MigrationGroupFacet';
import TenantLabel from '../components/TenantLabel';
import CampaignStepper from '../components/CampaignStepper';
import CampaignContextColumn from '../components/CampaignContextColumn';
import { DetailLayout } from '../components/ContextColumn';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';
import { useIdentityCsv } from '../components/identity/useIdentityCsv';
import { invalidateIdentityQueries } from '../lib/identityQueryKeys';
import { PageHeader, StatePill } from '../components/abPrimitives';
import { PageActions } from '../context/PageActionsContext';
import { deriveCampaignStatus } from '../lib/campaignStatus';
import { deriveCampaignLifecycle } from '../lib/campaignLifecycle';
import { ACCENT, IDENTIFIER, STROKE, TEXT } from '../theme/tokens';

// FD-281 (FD-ADR-031 §1) — campaign detail as TABS in the content column.
//
// This retires MigrationSubRail, the first of the three second full-height rails
// that grew beside the app rail and that ADR-031 supersedes. The tabs navigate
// the SAME child routes the sub-rail did, so tab state is the URL — already
// deep-linkable, already survives reload and back/forward, and the existing
// route tests keep meaning what they meant. A `?tab=` param would have been a
// second, weaker source of truth for something routing already answers.
//
// FD-491 rebuilt this into the CAMPAIGN SHELL the R-FD-051 mockups draw: the
// header, the six-step journey, the top-bar CTA, and the right-hand context
// column. Three things about that are worth stating where the next reader of
// this file will find them.
//
//   THE FOUR TAB PATHS ARE UNCHANGED. `overview`, `devices`, `identities`,
//   `tenants` — bookmarks, `Breadcrumb.tsx`, `NotFound` and every device-detail
//   back link resolve through them, and a shell rebuild is no reason to break a
//   URL an operator saved.
//
//   THE HEADER, THE STEPPER AND THE TABS ARE THE CAMPAIGN'S, NOT A TAB'S, so
//   they render on all four. The wave-state pill landed here under FD-467 on
//   exactly that argument; the journey is the same kind of fact.
//
//   EVERY HOOK RUNS BEFORE THE FIRST EARLY RETURN. FD-188 unmounted the tree to
//   a blank page when a shell component took a route-conditional hook, and this
//   component has three early returns (404, loading, error) sitting above the
//   render. `deriveCampaignLifecycle` is a plain function and is therefore safe
//   to call below them; `useState` is not.

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'devices', label: 'Devices' },
  { key: 'identities', label: 'Identities' },
  { key: 'tenants', label: 'Tenants' },
] as const;

const useStyles = makeStyles({
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    // FD-466 — the accent TEXT rung, from the token module rather than a
    // literal. Every one of these back links carried a hardcoded hex through
    // two accent retirements (sky-700, then Rubix orange) and the drift gate
    // caught them both times; taking the value from `ACCENT` is what stops a
    // third.
    color: ACCENT.pressed,
    fontSize: '13px',
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: '16px',
    rowGap: '8px',
    marginTop: '8px',
    fontSize: '13px',
    color: TEXT.secondary,
  },
  metaSeparator: {
    // FD-467 — was `#d4d4d8`, a zinc value from the retired palette. The
    // separator is decoration, so it takes the divider stroke rather than a
    // text rung.
    color: STROKE.base,
  },
});

export default function MigrationLayout() {
  const styles = useStyles();
  const { migrationId } = useParams<{ migrationId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // FD-491 — the group dialog is driven from the header's `···` menu as well as
  // from the Group facet itself. DECLARED HERE, above every early return,
  // because that is the FD-188 rule and this file is where it bit.
  //
  // Dialog state is LOCAL rather than in the URL, matching FD-478's reasoning on
  // the delete dialog: the ADR-015 rule keeps VIEW state in the URL so a filtered
  // page can be shared, and a shareable link that opens a pre-armed mutation
  // dialog is the opposite of what that rule is for.
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);

  // Gated live polling (FD-126) — carried here since the migration-detail query
  // moved onto the layout (FD-127). The device grid + overview metrics advance
  // hands-free during a live demo, but ONLY when gated on:
  //   • `?live=1` on the URL (preferred per-session switch), or
  //   • VITE_DEMO_REFRESH_MS set (staging .env only; unset in production).
  // Interval is the env value when a positive number, else a 5s default.
  // `refetchIntervalInBackground: false` keeps a backgrounded tab quiet.
  const demoRefreshMs = Number(import.meta.env.VITE_DEMO_REFRESH_MS);
  const demoRefreshEnabled = Number.isFinite(demoRefreshMs) && demoRefreshMs > 0;
  const liveGateOn = searchParams.get('live') === '1' || demoRefreshEnabled;
  const refetchInterval: number | false = liveGateOn
    ? demoRefreshEnabled
      ? demoRefreshMs
      : 5000
    : false;

  const query = useMigrationDetailQuery(migrationId, { refetchInterval });

  // The identity-mapping CSV actions, hoisted out of the tab body and into this
  // header's `···` menu (owner decision, 2026-09-28). The STATE has to live
  // here rather than in the menu, because a Fluent `MenuList` unmounts its
  // contents when the menu closes — a hidden file input mounted inside it would
  // be torn down by the very click that opens the picker.
  //
  // Declared above every early return, per the FD-188 rule this file exists to
  // remember. It is cheap on the three tabs that do not use it: a ref, three
  // idle useState slots and no request.
  const queryClient = useQueryClient();
  const csv = useIdentityCsv({
    migrationId: migrationId ?? '',
    onImported: useCallback(
      () => migrationId && invalidateIdentityQueries(queryClient, migrationId),
      [queryClient, migrationId],
    ),
  });

  if (query.isError && query.error instanceof ApiError && query.error.status === 404) {
    return (
      <div className="max-w-xl">
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Campaign not found</MessageBarTitle>
              No campaign exists with ID{' '}
              <span className="text-xs" style={IDENTIFIER}>
                {migrationId}
              </span>
              .
            </MessageBarBody>
            <MessageBarActions>
              <Link to="/migrations" className={styles.backLink}>
                Back to campaigns
              </Link>
            </MessageBarActions>
          </MessageBar>
      </div>
    );
  }

  if (query.isLoading) {
    return <DetailSkeleton />;
  }

  if (query.isError || !query.data) {
    return (
      <div className="max-w-xl">
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not load campaign</MessageBarTitle>
              {errorMessage(query.error)}
            </MessageBarBody>
            <MessageBarActions>
              <Button
                appearance="subtle"
                size="small"
                icon={<RefreshCw size={14} strokeWidth={1.5} />}
                onClick={() => query.refetch()}
              >
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
      </div>
    );
  }

  const { migration } = query.data;
  const devices = query.data.devices ?? [];

  // The active tab is READ FROM THE URL rather than held in state: a deep link
  // into /devices must open on Devices, and back/forward must move the tab.
  const activeTab =
    TABS.find((t) => location.pathname.endsWith(`/${t.key}`))?.key ?? 'overview';

  const isCloud = migration.migrationType === 'T2T';

  // The CSV actions belong to the Identities tab, so they appear in the `···`
  // only while it is the tab on screen — a campaign menu that always offered
  // "Import CSV" would be offering an identity control as a campaign one. The
  // type guard matches the tab's own: identity reconciliation is T2T/D2C only.
  const showIdentityCsv =
    activeTab === 'identities' &&
    (migration.migrationType === 'T2T' || migration.migrationType === 'D2C');

  // Derived from the WAVE credential plus device outcomes, never from
  // `Migrations.Status` (FD-269). Devices are passed so a revoked wave can say
  // "Completed with failures" rather than a flat "Completed".
  const campaignStatus = deriveCampaignStatus(migration, devices);

  // FD-490's six-step spine. Same discipline, same inputs, same refusal to read
  // `Migrations.Status`.
  const lifecycle = deriveCampaignLifecycle(migration, devices);
  const eventLogPath = `/migrations/${encodeURIComponent(migration.migrationId)}/events`;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/groups" className={styles.backLink}>
        <ArrowLeft size={14} strokeWidth={1.5} />
        Migration groups
      </Link>

      {/* FD-491 — the campaign's next step, in the TOP BAR.
          `PageActions` is a PORTAL, not a registration: nothing is stored, so
          nothing can go stale, and React unmounts it with the page that owns it
          (FD-470 records why the obvious context-holding-a-ReactNode shape
          either spins or leaks the previous route's button).

          It renders NOTHING when `nextAction` is null, which is what a finished
          campaign returns. FD-490 typed it nullable specifically to force that:
          a top bar that invents "View summary" on the page the operator is
          already looking at is a button that teaches them the console makes
          things up. */}
      {lifecycle.nextAction && (
        <PageActions>
          <Button
            appearance="primary"
            title={lifecycle.nextAction.reason}
            onClick={() => navigate(lifecycle.nextAction!.to)}
          >
            {lifecycle.nextAction.label}
          </Button>
        </PageActions>
      )}

      {/* FD-467 — the AirBridge PageHeader, and a WAVE-STATE PILL the header
          never carried.

          FD-502 — AND NO FACT ROW UNDER IT. The header carried Type · Wave ·
          Group · Devices assigned · Created · Last updated, and every one of
          those is a row in Overview's "Campaign details" panel or a tile above
          it, one scroll below. Two renderings of the same six facts, three
          inches apart, is the redundancy the owner asked to remove — and two
          renderings of one figure is also how they come to disagree.

          WHAT THAT COSTS, so it is a decision rather than an accident: Type and
          Wave now appear on the OVERVIEW TAB ONLY. Group does not — the context
          column carries it under "Related resources" on all four tabs — and nor
          does the wave state, which is the pill beside the title and the panel
          in the context column. If Type or Wave is ever wanted on every tab, the
          context column is where it goes; putting the row back would restore the
          duplication rather than fix it. An operator on the Devices or Identities tab could not
          previously tell whether the wave was even activated without going back
          to Overview; the state is the campaign's, not Overview's, so it belongs
          beside the name on every tab. It is DERIVED (`deriveCampaignStatus`)
          rather than read from `Migrations.Status`, which is written "active" at
          creation and never updated (FD-269). */}
      <PageHeader
        title={migration.migrationName}
        badge={<StatePill state={campaignStatus.state}>{campaignStatus.label}</StatePill>}
        subtitle={
          // The conduit — where these devices come from and go to. It sits on
          // every tab rather than only on Overview because it is the campaign's
          // identity, not one page's content. A campaign always has a
          // destination; only Cloud has a source tenant, and for Hybrid the
          // origin is the on-premises domain — a real place, named as one,
          // because an empty left-hand side would read as missing data.
          <span className="inline-flex flex-wrap items-center gap-2">
            {isCloud ? (
              <TenantLabel
                domain={migration.sourceTenantDomain}
                tenantId={migration.sourceTenantId}
              />
            ) : (
              <span>On-premises domain</span>
            )}
            <RouteFlight />
            <TenantLabel domain={migration.destTenantDomain} tenantId={migration.destTenantId} />
          </span>
        }
        actions={
          /* FD-491 — the `···` overflow. TWO ITEMS, BOTH REAL (FD-ADR-043 D4):
             the group dialog this header already owns, and the campaign's event
             log, which is a route. The mockups also draw Pause, Duplicate and
             Archive; FD-ADR-031 §6 refused all three because nothing on the wire
             performs them, and `abort-wave` — the nearest real operation — is
             irreversible and terminal-stamps every device. A friendly label over
             that is a data-loss defect, not a shortcut. */
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                aria-label="Campaign actions"
                icon={<MoreHorizontal size={18} strokeWidth={1.5} />}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<FolderTree size={16} strokeWidth={1.5} />}
                  onClick={() => setGroupDialogOpen(true)}
                >
                  Assign to group
                </MenuItem>
                <MenuItem
                  icon={<ScrollText size={16} strokeWidth={1.5} />}
                  onClick={() => navigate(eventLogPath)}
                >
                  View event log
                </MenuItem>
                {/* Owner decision, 2026-09-28 — the CSV actions move up here
                    from the tab body, UNDER THEIR OWN HEADER. The two items
                    above act on the campaign; these two act on its identity
                    mappings, and an unlabelled four-item menu would read as
                    four campaign actions. */}
                {showIdentityCsv && (
                  <>
                    <MenuDivider />
                    <MenuGroup>
                      <MenuGroupHeader>Identity mappings</MenuGroupHeader>
                      <MenuItem
                        icon={<Download size={16} strokeWidth={1.5} />}
                        disabled={csv.busy !== null}
                        onClick={csv.downloadTemplate}
                      >
                        Download template
                      </MenuItem>
                      <MenuItem
                        icon={<Upload size={16} strokeWidth={1.5} />}
                        disabled={csv.busy !== null}
                        onClick={csv.openFilePicker}
                      >
                        Import CSV
                      </MenuItem>
                    </MenuGroup>
                  </>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        }
      />

      {/* The file input and the import's result bars — OUTSIDE the menu, which
          unmounts its contents on close, and outside the `<Outlet/>`, which
          remounts on every tab change. The bars state what an import did
          (added / updated / rejected, with per-row reasons), so they sit where
          the operator who triggered it is looking. */}
      {csv.fileInput}
      {csv.feedback}

      {/* FD-502 — the group dialog, with NO trigger of its own.
          The header's fact row used to carry the Group facet, whose button both
          displayed the group and opened this dialog. The row is gone (see the
          header note above) and the `···` menu's "Assign to group" is now the
          only way in, so the dialog has to exist without it. Rendering it
          triggerless here keeps ONE instance owned by this component — the
          property `MigrationLayoutShell.test.tsx` asserts — rather than a second
          dialog mounted beside the menu, which is how two mutation surfaces
          against one row come to disagree about what was saved. */}
      <MigrationGroupFacet
        migrationId={migration.migrationId}
        migrationGroupId={migration.migrationGroupId}
        migrationGroupName={migration.migrationGroupName}
        showTrigger={false}
        open={groupDialogOpen}
        onOpenChange={setGroupDialogOpen}
      />

      <CampaignStepper view={lifecycle} />

      <div style={{ borderBottom: `1px solid ${STROKE.base}` }}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data) => navigate(`/migrations/${migrationId}/${data.value}`)}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key}>
              {t.label}
            </Tab>
          ))}
        </TabList>
      </div>

      {/* FD-491 — the context column, mounted beside the TAB CONTENT rather
          than beside the header, so the header and the journey still read across
          the page; `DetailLayout` collapses it below the content under 1280px.

          FD-503 — AND ONLY ON OVERVIEW. It rendered on all four tabs, which was
          right for the facts it carries (the wave credential is the campaign's,
          not Overview's) and wrong for the three tabs that are TABLES: Devices,
          Identities and Tenants each gave up 320px of horizontal room to restate
          state they cannot act on, on a grid that was already scrolling
          sideways. Overview is where the campaign's record lives, so that is
          where its record column lives.

          WHAT SURVIVES ON THE OTHER THREE TABS, so this is a trim rather than a
          loss: the wave-state pill sits beside the title on every tab (FD-467),
          the six-step stepper is above the tabs, and the campaign's group and
          both tenants are Overview's "Campaign details" and the column's
          "Related resources" — one click away, not gone.

          `DetailLayout` renders `children` bare when `context` is undefined, so
          the other three tabs get the full width rather than an empty 320px
          gutter. */}
      <DetailLayout
        context={
          activeTab === 'overview' ? (
            <CampaignContextColumn
              migration={migration}
              devices={devices}
              waveDefaults={query.data.waveDefaults}
            />
          ) : undefined
        }
      >
        <Outlet />
      </DetailLayout>
    </div>
  );
}

// Loading placeholder — folded in from the retired MigrationDetailPage (FD-127).
function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton>
        <SkeletonItem size={16} style={{ width: '120px' }} />
      </Skeleton>
      <div style={{ display: 'flex', flexDirection: 'column', rowGap: '12px' }}>
        <Skeleton>
          <SkeletonItem size={32} style={{ width: '40%' }} />
        </Skeleton>
        <Skeleton>
          <SkeletonItem size={16} style={{ width: '70%' }} />
        </Skeleton>
      </div>
      <Skeleton>
        <SkeletonItem size={128} style={{ width: '100%' }} />
      </Skeleton>
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
